import { createContext, useContext, type CSSProperties, type ReactNode } from 'react';
import { COMMON } from '@locales/id/messages';
import { useLoadingGate } from './useLoadingGate';

/** Async already waited before showing a placeholder; standalone placeholders (Suspense) wait themselves. */
export const SkeletonDelay = createContext(true);

/** One shimmering shape. */
export function Skeleton({ shape = 'line', width, height, className = '', style }: { shape?: 'line' | 'block' | 'circle'; width?: CSSProperties['width']; height?: CSSProperties['height']; className?: string; style?: CSSProperties }) {
  return <span className={`sk sk-${shape} ${className}`.trim()} style={{ width, height, ...style }} aria-hidden="true" />;
}

/** Announces loading to screen readers; appears only after a short wait unless Async already waited. */
function SkeletonFrame({ label = COMMON.loading, className = '', children }: { label?: string; className?: string; children: ReactNode }) {
  const delayed = useContext(SkeletonDelay);
  const visible = useLoadingGate(true);
  if (delayed && !visible) return null;
  return (
    <div className={`skeleton ${className}`.trim()} role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

const CELL_WIDTHS = ['62%', '78%', '54%', '40%', '66%', '48%', '58%'];

/** Placeholder with the layout of a DataTable: same cell padding, row height and phone card fallback. */
export function SkeletonTable({ columns = 4, rows = 6, label }: { columns?: number; rows?: number; label?: string }) {
  const cols = Array.from({ length: columns }, (_, i) => i);
  return (
    <SkeletonFrame label={label} className="skeleton-table">
      <div className="table-wrap">
        <table className="table responsive" aria-hidden="true">
          <thead>
            <tr>{cols.map((i) => <th key={i}><Skeleton width={i === 0 ? 72 : 56} className="sk-head" /></th>)}</tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, r) => (
              <tr key={r}>
                {cols.map((i) =>
                  i === 0 ? (
                    <td key={i} className="lead"><Skeleton width={CELL_WIDTHS[r % CELL_WIDTHS.length]} /><Skeleton width="38%" className="sk-sub" /></td>
                  ) : (
                    <td key={i} data-label=" "><Skeleton width={CELL_WIDTHS[(r + i) % CELL_WIDTHS.length]} /></td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SkeletonFrame>
  );
}

/** Placeholder with the layout of a `.list` of rows (title + meta, optional trailing chip). */
export function SkeletonCards({ count = 4, label, framed }: { count?: number; label?: string; framed?: boolean }) {
  return (
    <SkeletonFrame label={label} className={framed ? 'skeleton-cards framed' : 'skeleton-cards'}>
      <ul className="list" aria-hidden="true">
        {Array.from({ length: count }, (_, i) => (
          <li key={i} className="list-row">
            <div className="grow">
              <Skeleton width={CELL_WIDTHS[i % CELL_WIDTHS.length]} />
              <Skeleton width="44%" className="sk-sub" />
            </div>
            <Skeleton width={64} height={22} className="sk-pill" />
          </li>
        ))}
      </ul>
    </SkeletonFrame>
  );
}

/** Placeholder for a row of figures (stages, facts). */
export function SkeletonStat({ count = 3, label }: { count?: number; label?: string }) {
  return (
    <SkeletonFrame label={label} className="skeleton-stats">
      <StatShapes count={count} />
    </SkeletonFrame>
  );
}

/** Whole-page placeholder: title, a row of figures, a content block. */
export function SkeletonPage({ label }: { label?: string }) {
  return (
    <SkeletonFrame label={label} className="skeleton-page">
      <Skeleton className="sk-title" />
      <span className="card skeleton-stats-card"><StatShapes count={3} /></span>
      <Skeleton shape="block" height={320} />
    </SkeletonFrame>
  );
}
function StatShapes({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className="skeleton-stat">
          <Skeleton width="50%" />
          <Skeleton width="70%" height={28} />
        </span>
      ))}
    </>
  );
}

/** A single block (a card that is loading). */
export function SkeletonBlock({ height = 120, label }: { height?: number; label?: string }) {
  return (
    <SkeletonFrame label={label}>
      <Skeleton shape="block" height={height} />
    </SkeletonFrame>
  );
}
